(function () {
  'use strict';

  const API_BASE = 'https://api.treatmyocd.com';

  /** Google Ads “Book call” conversion snippet (Google tag / gtag) — fires after booking API succeeds. */
  const PIP_GOOGLE_ADS_BOOKING_SEND_TO = 'AW-859584545/95vkCICo67McEKHw8JkD';

  // ── Pure utilities (shared across all instances) ─────────────────────────────

  const TZ_DISPLAY = {
    'America/New_York':    'Eastern Time \u2013 New York City',
    'America/Detroit':     'Eastern Time \u2013 Detroit',
    'America/Chicago':     'Central Time \u2013 Chicago',
    'America/Denver':      'Mountain Time \u2013 Denver',
    'America/Phoenix':     'Mountain Time \u2013 Phoenix',
    'America/Los_Angeles': 'Pacific Time \u2013 Los Angeles',
    'America/Anchorage':   'Alaska Time \u2013 Anchorage',
    'Pacific/Honolulu':    'Hawaii Time \u2013 Honolulu',
  };

  // ── State → timezone lookup ──────────────────────────────────────────────────
  const STATE_TZ = {
    CT:'America/New_York', DE:'America/New_York', FL:'America/New_York',
    GA:'America/New_York', IN:'America/New_York', ME:'America/New_York',
    MD:'America/New_York', MA:'America/New_York', MI:'America/Detroit',
    NH:'America/New_York', NJ:'America/New_York', NY:'America/New_York',
    NC:'America/New_York', OH:'America/New_York', PA:'America/New_York',
    RI:'America/New_York', SC:'America/New_York', VT:'America/New_York',
    VA:'America/New_York', WV:'America/New_York', DC:'America/New_York',
    AL:'America/Chicago',  AR:'America/Chicago',  IL:'America/Chicago',
    IA:'America/Chicago',  KS:'America/Chicago',  KY:'America/Chicago',
    LA:'America/Chicago',  MN:'America/Chicago',  MS:'America/Chicago',
    MO:'America/Chicago',  NE:'America/Chicago',  ND:'America/Chicago',
    OK:'America/Chicago',  SD:'America/Chicago',  TN:'America/Chicago',
    TX:'America/Chicago',  WI:'America/Chicago',
    AZ:'America/Phoenix',  CO:'America/Denver',   ID:'America/Denver',
    MT:'America/Denver',   NM:'America/Denver',   UT:'America/Denver',
    WY:'America/Denver',
    CA:'America/Los_Angeles', NV:'America/Los_Angeles',
    OR:'America/Los_Angeles', WA:'America/Los_Angeles',
    AK:'America/Anchorage', HI:'Pacific/Honolulu',
    'connecticut':'America/New_York', 'delaware':'America/New_York',
    'florida':'America/New_York', 'georgia':'America/New_York',
    'indiana':'America/New_York', 'maine':'America/New_York',
    'maryland':'America/New_York', 'massachusetts':'America/New_York',
    'michigan':'America/Detroit', 'new hampshire':'America/New_York',
    'new jersey':'America/New_York', 'new york':'America/New_York',
    'north carolina':'America/New_York', 'ohio':'America/New_York',
    'pennsylvania':'America/New_York', 'rhode island':'America/New_York',
    'south carolina':'America/New_York', 'vermont':'America/New_York',
    'virginia':'America/New_York', 'west virginia':'America/New_York',
    'district of columbia':'America/New_York',
    'alabama':'America/Chicago', 'arkansas':'America/Chicago',
    'illinois':'America/Chicago', 'iowa':'America/Chicago',
    'kansas':'America/Chicago', 'kentucky':'America/Chicago',
    'louisiana':'America/Chicago', 'minnesota':'America/Chicago',
    'mississippi':'America/Chicago', 'missouri':'America/Chicago',
    'nebraska':'America/Chicago', 'north dakota':'America/Chicago',
    'oklahoma':'America/Chicago', 'south dakota':'America/Chicago',
    'tennessee':'America/Chicago', 'texas':'America/Chicago',
    'wisconsin':'America/Chicago',
    'arizona':'America/Phoenix', 'colorado':'America/Denver',
    'idaho':'America/Denver', 'montana':'America/Denver',
    'new mexico':'America/Denver', 'utah':'America/Denver',
    'wyoming':'America/Denver',
    'california':'America/Los_Angeles', 'nevada':'America/Los_Angeles',
    'oregon':'America/Los_Angeles', 'washington':'America/Los_Angeles',
    'alaska':'America/Anchorage', 'hawaii':'Pacific/Honolulu',
  };

  function tzForState(code) {
    if (!code) return 'America/Chicago';
    return STATE_TZ[code] || STATE_TZ[code.toLowerCase()] || 'America/Chicago';
  }

  // Returns { prefix: "Today", rest: " April 24, 2025 (Eastern Time – New York City)" }
  function buildDateContext(dateStr, tz) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    let prefix;
    if (dateObj.getTime() === today.getTime()) prefix = 'Today';
    else if (dateObj.getTime() === tomorrow.getTime()) prefix = 'Tomorrow';
    else prefix = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
    const datePart = dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return { prefix, rest: ` ${datePart} (${TZ_DISPLAY[tz] || tz})` };
  }

  // Normalize state list — handles arrays of objects or plain strings
  function parseStates(data) {
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      if (typeof item === 'string') return { value: item, label: item };
      const value = item.abbreviation || item.code || item.id || item.value || item.slug || '';
      const label = item.name || item.label || value;
      return { value: String(value), label: String(label) };
    }).filter(s => s.value);
  }

  // Parse insurance list — API shape: [{ label, value, accepted }, ...]
  function parseInsurances(data) {
    if (!Array.isArray(data)) return [];
    return data
      .map((item) => ({
        value: String(item.value || ''),
        label: String(item.label || item.value || ''),
        accepted: item.accepted === true,
      }))
      .filter((i) => i.value);
  }

  function ordinalDay(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  // Tracks how many PIP overlays are currently open so scroll-lock is only
  // released when the last one closes.
  let openCount = 0;

  // ── Per-instance initialiser ─────────────────────────────────────────────────

  function initPIP(root) {

    // Warm cache for background + every <img> in the overlay so assets are ready before open
    (function preloadPipImages() {
      const pipBgUrl = root.dataset.pipBgUrl;
      if (pipBgUrl) {
        const bg = new Image();
        bg.src = pipBgUrl;
      }
      root.querySelectorAll('img[src]').forEach((el) => {
        const url = el.currentSrc || el.src;
        if (!url) return;
        const pre = new Image();
        pre.src = url;
      });
    }());

    // Config from data attributes set in the Liquid markup
    const autoOpen      = root.dataset.autoOpen === 'true';
    const autoOpenDelay = parseInt(root.dataset.autoOpenDelay, 10) || 0;

    // Trigger button lives outside the root; linked by data-pip-target="${root.id}"
    const $triggerBtn = document.querySelector(`.pip-trigger[data-pip-target="${root.id}"]`);

    function $id(id) { return root.querySelector('#' + id); }

    // ── DOM refs ────────────────────────────────────────────────────────────────
    const $closeBtn         = root.querySelector('.pip-nav__close');
    const $backBtn          = root.querySelector('.pip-nav__back');

    const $stateSelect      = $id('pip-state-select');
    const $stateWrapper     = $id('pip-state-wrapper');
    const $stateError       = $id('pip-state-error');

    const $insuranceSelect  = $id('pip-insurance-select');
    const $insuranceWrapper = $id('pip-insurance-wrapper');
    const $insuranceError   = $id('pip-insurance-error');

    const $medicareCheckbox = $id('pip-medicare-checkbox');
    const $nextBtn1         = $id('pip-next-btn-1');
    const $progress         = $id('pip-progress');

    // ── Instance state ──────────────────────────────────────────────────────────
    const formData = {
      stateCode: null,
      stateName: null,
      insuranceId: null,
      insuranceName: null,
      insuranceAccepted: null,
      usingMedicaid: false,
      bookingTz: null,
      bookingDate: null,
      bookingPeriod: null,
      bookingTime: null,
      bookingTimeDisplay: null,
      contact: { firstName: '', lastName: '', email: '', phone: '' },
      isCallback: false,
    };

    let currentStage = 'intro';
    let stageHistory = [];
    let statesLoaded = false;
    let allTimeSlots = { morning: [], evening: [] };

    // ── Helpers ─────────────────────────────────────────────────────────────────

    function setDateContext(elId, dateStr, tz) {
      const { prefix, rest } = buildDateContext(dateStr, tz);
      $id(elId).innerHTML = `<strong>${prefix}</strong>${rest}`;
    }

    function setLoading($wrapper, loading) {
      $wrapper.classList.toggle('is-loading', loading);
    }

    function showError($el, show) {
      $el.classList.toggle('is-visible', show);
    }

    function resetSelect($select, placeholder) {
      $select.innerHTML = `<option value="">${placeholder}</option>`;
    }

    function stageForTracking(stageId) {
      if (stageId === 'intro') return 'partner-intro';
      if (stageId === '1') return 'insurance-check';
      if (stageId === 'booking-confirm') {
        return formData.isCallback ? 'booking-confirm-immediate-call' : 'booking-confirm-scheduled-call';
      }
      return stageId;
    }

    // ── Open / close ─────────────────────────────────────────────────────────────
    function openPopup() {
      root.classList.add('is-open');
      openCount++;
      document.body.style.overflow = 'hidden';

      if (window.posthog) {
        window.posthog.capture(`step:${stageForTracking(currentStage)}`, {
          source: 'partner-popup',
        });
      }

      if (!statesLoaded) {
        loadStates()
          .then(() => {
            statesLoaded = true;
            if (currentStage === '1') $stateSelect.focus();
          })
          .catch((err) => {
            statesLoaded = false;
            console.error('[partner-intake] openPopup: states failed to load, will retry on next open:', err);
          });
      } else if (currentStage === '1') {
        $stateSelect.focus();
      }

      if (currentStage === 'intro') {
        const $introBtn = $id('pip-intro-next-btn');
        if ($introBtn) $introBtn.focus();
      }
    }

    function closePopup(trackClose = true) {
      if (trackClose && window.posthog) {
        window.posthog.capture(`closed:${stageForTracking(currentStage)}`, {
          source: 'partner-popup',
        });
      }
      root.classList.remove('is-open');
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) document.body.style.overflow = '';
    }

    $closeBtn.addEventListener('click', closePopup);

    root.addEventListener('click', (e) => {
      if (e.target === root) closePopup();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && root.classList.contains('is-open')) closePopup();
    });

    if ($triggerBtn) $triggerBtn.addEventListener('click', openPopup);

    // ── Stage navigation ─────────────────────────────────────────────────────────
    const BOOKING_STEPS = {
      'booking-dates':       1,
      'booking-time-of-day': 2,
      'booking-times':       3,
      'booking-confirm':     4,
    };

    function updateProgress(stageId) {
      const step = BOOKING_STEPS[stageId];
      if (step === undefined || formData.isCallback) {
        $progress.hidden = true;
        return;
      }
      $progress.hidden = false;

      $progress.querySelectorAll('.pip-progress__node').forEach(($node) => {
        const n = parseInt($node.dataset.step, 10);
        $node.classList.toggle('is-done',   n < step);
        $node.classList.toggle('is-active', n === step);
      });

      $progress.querySelectorAll('.pip-progress__connector').forEach(($line) => {
        $line.classList.toggle('is-done', parseInt($line.dataset.after, 10) < step);
      });
    }

    function goToStage(id) {
      stageHistory.push(currentStage);
      root.querySelectorAll('.pip-stage').forEach(($s) => $s.classList.remove('is-active'));
      const $next = root.querySelector(`#pip-stage-${id}`);
      if ($next) $next.classList.add('is-active');
      currentStage = id;
      $backBtn.hidden = stageHistory.length === 0;
      updateProgress(id);

      if (window.posthog) {
        window.posthog.capture(`step:${stageForTracking(id)}`, {
          source: 'partner-popup',
        });
      }
    }

    function goBack() {
      if (stageHistory.length === 0) return;
      const prev = stageHistory.pop();
      root.querySelectorAll('.pip-stage').forEach(($s) => $s.classList.remove('is-active'));
      const $prev = root.querySelector(`#pip-stage-${prev}`);
      if ($prev) $prev.classList.add('is-active');
      currentStage = prev;
      $backBtn.hidden = stageHistory.length === 0;
      updateProgress(prev);
    }

    $backBtn.addEventListener('click', goBack);

    // ── Load states ──────────────────────────────────────────────────────────────
    async function loadStates() {
      setLoading($stateWrapper, true);
      $stateSelect.disabled = true;

      try {
        const res = await fetch(`${API_BASE}/v1/partner_intake_calls/states`, { redirect: 'follow' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        const list = Array.isArray(raw) ? raw : (raw.data || raw.states || raw.results || Object.values(raw)[0] || []);
        const states = parseStates(list);
        resetSelect($stateSelect, 'Location');
        states.forEach(({ value, label }) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = label;
          $stateSelect.appendChild(opt);
        });
      } catch (err) {
        console.error('[partner-intake] Failed to load states:', err);
        throw err;
      } finally {
        $stateSelect.disabled = false;
        setLoading($stateWrapper, false);
      }
    }

    // ── Load insurances for selected state ───────────────────────────────────────
    async function loadInsurances(stateCode) {
      resetSelect($insuranceSelect, 'Loading\u2026');
      $insuranceSelect.disabled = true;
      setLoading($insuranceWrapper, true);

      try {
        const url = `${API_BASE}/v1/partner_intake_calls/insurances?state=${encodeURIComponent(stateCode)}`;
        const res = await fetch(url, { redirect: 'follow' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        const list = Array.isArray(raw) ? raw : (raw.data || raw.insurances || raw.results || Object.values(raw)[0] || []);
        const insurances = parseInsurances(list);

        resetSelect($insuranceSelect, 'Select insurance');
        insurances.forEach(({ value, label, accepted }) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = label;
          opt.dataset.accepted = accepted ? 'true' : 'false';
          $insuranceSelect.appendChild(opt);
        });
        $insuranceSelect.disabled = false;
      } catch (err) {
        console.error('[partner-intake] Failed to load insurances:', err);
        resetSelect($insuranceSelect, 'Could not load \u2014 try again');
        $insuranceSelect.disabled = false;
      } finally {
        setLoading($insuranceWrapper, false);
      }
    }

    // ── Event listeners ──────────────────────────────────────────────────────────
    $stateSelect.addEventListener('change', () => {
      const val = $stateSelect.value;
      showError($stateError, false);

      if (!val) {
        formData.stateCode = null;
        formData.stateName = null;
        resetSelect($insuranceSelect, 'Select location first');
        $insuranceSelect.disabled = true;
        return;
      }

      formData.stateCode = val;
      formData.stateName = $stateSelect.options[$stateSelect.selectedIndex].text;
      $medicareCheckbox.checked = false;
      formData.usingMedicaid = false;
      loadInsurances(val);
    });

    $insuranceSelect.addEventListener('change', () => {
      const val = $insuranceSelect.value;
      showError($insuranceError, false);
      const selectedOpt = $insuranceSelect.options[$insuranceSelect.selectedIndex];
      formData.insuranceId = val || null;
      formData.insuranceName = val ? selectedOpt.text : null;
      formData.insuranceAccepted = val ? selectedOpt.dataset.accepted === 'true' : null;
    });

    $medicareCheckbox.addEventListener('change', () => {
      formData.usingMedicaid = $medicareCheckbox.checked;
    });

    const $introNextBtn = $id('pip-intro-next-btn');
    if ($introNextBtn) {
      $introNextBtn.addEventListener('click', () => {
        goToStage('1');
        setTimeout(() => {
          $stateSelect.focus();
        }, 0);
      });
    }

    // ── Stage 1 → Next ───────────────────────────────────────────────────────────
    $nextBtn1.addEventListener('click', () => {
      let valid = true;

      if (!formData.stateCode) {
        showError($stateError, true);
        valid = false;
      }

      if (!formData.usingMedicaid && !formData.insuranceId) {
        showError($insuranceError, true);
        valid = false;
      }

      if (!valid) return;

      document.dispatchEvent(new CustomEvent('pip:stage1Complete', { detail: { ...formData } }));

      if (formData.usingMedicaid || !formData.insuranceAccepted) {
        goToStage('insurance-no');
      } else {

        goToStage('insurance-yes');
      }
    });

    // ── insurance-yes: instant callback ──────────────────────────────────────────
    $id('pip-speak-now-btn').addEventListener('click', () => {
      formData.isCallback = true;
      $id('pip-booking-summary').style.display = 'none';
      root.querySelector('.pip-booking-subheader').style.display = '';
      $id('pip-book-now-btn').textContent = 'Confirm';
      goToStage('booking-confirm');
    });

    // ── insurance-yes: schedule for later ────────────────────────────────────────
    $id('pip-schedule-later-btn').addEventListener('click', () => {
      formData.isCallback = false;
      const tz = tzForState(formData.stateCode);
      formData.bookingTz = tz;
      const $tzSelect = $id('pip-tz-select');
      const tzValues = [...$tzSelect.options].map((o) => o.value);
      $tzSelect.value = tzValues.includes(tz) ? tz : 'America/Chicago';
      goToStage('booking-dates');
      loadAvailableDates($tzSelect.value);
    });

    // ── Timezone change re-fetches dates ──────────────────────────────────────────
    $id('pip-tz-select').addEventListener('change', function () {
      formData.bookingTz = this.value;
      loadAvailableDates(this.value);
    });

    // ── Load available dates ───────────────────────────────────────────────────────
    async function loadAvailableDates(tz) {
      const $list = $id('pip-date-list');
      $list.innerHTML = '<div class="pip-date-list__loading">Loading available dates\u2026</div>';

      try {
        const url = `${API_BASE}/v1/partner_intake_calls/availability/dates?tz=${encodeURIComponent(tz)}&days=5`;
        const res = await fetch(url, { redirect: 'follow' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();

        const list = Array.isArray(raw) ? raw : (raw.data || raw.dates || raw.results || []);

        if (!list.length) {
          $list.innerHTML = '<p class="pip-date-list__empty">No available dates at this time.</p>';
          return;
        }

        $list.innerHTML = '';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        list.forEach((item) => {
          const dateStr = typeof item === 'string' ? item : (item.date || item.value || '');
          const displayOverride = typeof item === 'object' ? (item.display || item.label || '') : '';

          let label = displayOverride;
          if (!label && dateStr) {
            const [y, m, d] = dateStr.split('-').map(Number);
            const dateObj = new Date(y, m - 1, d);
            const formatted = dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            if (dateObj.getTime() === today.getTime()) label = `Today (${formatted})`;
            else if (dateObj.getTime() === tomorrow.getTime()) label = `Tomorrow (${formatted})`;
            else label = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
          }

          const $btn = document.createElement('button');
          $btn.className = 'pip-date-card';
          $btn.textContent = label || dateStr;
          $btn.dataset.date = dateStr;
          $btn.addEventListener('click', () => {
            formData.bookingDate = dateStr;
            root.querySelectorAll('.pip-date-card').forEach(($c) => $c.classList.remove('is-selected'));
            $btn.classList.add('is-selected');
            loadTimeSlotsForDate(dateStr, formData.bookingTz);
          });
          $list.appendChild($btn);
        });
      } catch (err) {
        console.error('[partner-intake] Failed to load dates:', err);
        $id('pip-date-list').innerHTML =
          '<p class="pip-date-list__empty">Could not load dates \u2014 please try again.</p>';
      }
    }

    // ── Load time slots for a date ────────────────────────────────────────────────

    function isSlotPast(slotValue, date, tz) {
      const now = new Date();
      const isISO = slotValue && (slotValue.includes('T') || slotValue.includes('Z') || /[+-]\d{2}:\d{2}$/.test(slotValue));
      if (isISO) return new Date(slotValue) <= now;

      const todayInTz = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(now);
      if (date !== todayInTz) return false;

      const match = (slotValue || '').match(/(\d{1,2}):(\d{2})/);
      if (!match) return false;
      const parts = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric', minute: 'numeric', hour12: false, timeZone: tz,
      }).formatToParts(now);
      const nowH = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
      const nowM = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
      const slotH = parseInt(match[1], 10);
      const slotM = parseInt(match[2], 10);
      return slotH < nowH || (slotH === nowH && slotM <= nowM);
    }

    async function loadTimeSlotsForDate(date, tz) {
      allTimeSlots = { morning: [], evening: [] };

      try {
        const url = `${API_BASE}/v1/partner_intake_calls/availability/dates/${date}?tz=${encodeURIComponent(tz)}`;
        const res = await fetch(url, { redirect: 'follow' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();

        const list = Array.isArray(raw) ? raw : (raw.data || raw.slots || raw.times || raw.results || []);

        list.forEach((item) => {
          let value, hourInTz, display;

          if (typeof item === 'string' && (item.includes('T') || item.includes('Z') || /[+-]\d{2}:\d{2}$/.test(item))) {
            const d = new Date(item);
            value = item;
            hourInTz = parseInt(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: tz }).format(d), 10);
            display = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz, timeZoneName: 'short' }).format(d);
          } else if (typeof item === 'string') {
            const match = item.match(/(\d{1,2}):(\d{2})/);
            hourInTz = match ? parseInt(match[1], 10) : 0;
            const [y, mo, dy] = date.split('-').map(Number);
            const d = new Date(y, mo - 1, dy, hourInTz, match ? parseInt(match[2], 10) : 0);
            value = item;
            display = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz, timeZoneName: 'short' }).format(d);
          } else {
            const timeStr = item.time || item.datetime || item.slot || item.value || '';
            if (timeStr.includes('T') || timeStr.includes('Z') || /[+-]\d{2}:\d{2}$/.test(timeStr)) {
              const d = new Date(timeStr);
              value = timeStr;
              hourInTz = parseInt(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: tz }).format(d), 10);
              display = item.display || item.label || new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz, timeZoneName: 'short' }).format(d);
            } else {
              const match = timeStr.match(/(\d{1,2}):(\d{2})/);
              hourInTz = match ? parseInt(match[1], 10) : 0;
              const [y, mo, dy] = date.split('-').map(Number);
              const d = new Date(y, mo - 1, dy, hourInTz, match ? parseInt(match[2], 10) : 0);
              value = timeStr;
              display = item.display || item.label || new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz, timeZoneName: 'short' }).format(d);
            }
          }

          const past = isSlotPast(value, date, tz);
          const slot = { value, display, past };
          if (hourInTz < 12) allTimeSlots.morning.push(slot);
          else allTimeSlots.evening.push(slot);
        });
      } catch (err) {
        console.error('[partner-intake] Failed to load time slots:', err);
      }

      const morningAvailable = allTimeSlots.morning.some(s => !s.past);
      const eveningAvailable = allTimeSlots.evening.some(s => !s.past);
      $id('pip-period-morning').disabled = !morningAvailable;
      $id('pip-period-evening').disabled = !eveningAvailable;

      setDateContext('pip-tod-date-context', date, tz);
      goToStage('booking-time-of-day');
    }

    // ── Period selection (Morning / Evening) ──────────────────────────────────────
    $id('pip-period-morning').addEventListener('click', () => {
      formData.bookingPeriod = 'morning';
      renderTimeSlots(allTimeSlots.morning);
    });

    $id('pip-period-evening').addEventListener('click', () => {
      formData.bookingPeriod = 'evening';
      renderTimeSlots(allTimeSlots.evening);
    });

    function renderTimeSlots(slots) {
      setDateContext('pip-times-date-context', formData.bookingDate, formData.bookingTz);
      const $list = $id('pip-time-list');
      $list.innerHTML = '';

      const bookable = slots.filter(s => !s.past);
      if (!bookable.length) {
        $list.innerHTML = '<p class="pip-date-list__empty">No available times for this period.</p>';
        goToStage('booking-times');
        return;
      }

      slots.forEach(({ value, display, past }) => {
        const $btn = document.createElement('button');
        $btn.className = 'pip-date-card';
        $btn.textContent = display;
        $btn.dataset.slot = value;
        if (past) {
          $btn.disabled = true;
        } else {
          $btn.addEventListener('click', () => {
            formData.bookingTime = value;
            formData.bookingTimeDisplay = display;
            root.querySelectorAll('#pip-time-list .pip-date-card').forEach(($c) => $c.classList.remove('is-selected'));
            $btn.classList.add('is-selected');
            showBookingConfirm();
          });
        }
        $list.appendChild($btn);
      });

      goToStage('booking-times');
    }

    function showBookingConfirm() {
      const $summary = $id('pip-booking-summary');
      $summary.style.display = '';
      root.querySelector('.pip-booking-subheader').style.display = 'none';
      $id('pip-book-now-btn').textContent = 'Book now';
      const [y, m, d] = formData.bookingDate.split('-').map(Number);
      const dateObj = new Date(y, m - 1, d);
      const weekday = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
      const month   = dateObj.toLocaleDateString('en-US', { month: 'long' });
      const timeRaw = formData.bookingTimeDisplay || formData.bookingTime || '';
      const time    = timeRaw.replace(/\s(AM|PM)/, '$1');
      $id('pip-confirm-datetime').textContent =
        `${weekday} ${ordinalDay(d)} ${month} at ${time}`;
      goToStage('booking-confirm');
    }

    // ── Book now ──────────────────────────────────────────────────────────────────
    $id('pip-book-now-btn').addEventListener('click', async () => {
      const $fn      = $id('pip-first-name');
      const $ln      = $id('pip-last-name');
      const $email   = $id('pip-email');
      const $phone   = $id('pip-phone');
      const $bookErr = $id('pip-book-error');

      const isValidEmail = (v) => {
        const at = v.indexOf('@');
        if (at <= 0 || at !== v.lastIndexOf('@') || at === v.length - 1) return false;
        const domain = v.slice(at + 1);
        const dot = domain.indexOf('.');
        return dot > 0 && dot < domain.length - 1;
      };

      let valid = true;
      const setErr = (id, show) => { $id(id).classList.toggle('is-visible', show); };

      setErr('pip-first-name-error', !$fn.value.trim());       if (!$fn.value.trim())              valid = false;
      setErr('pip-last-name-error',  !$ln.value.trim());       if (!$ln.value.trim())              valid = false;
      setErr('pip-email-error', !isValidEmail($email.value));  if (!isValidEmail($email.value))    valid = false;
      setErr('pip-phone-error', !$phone.value.trim());         if (!$phone.value.trim())           valid = false;

      if (!valid) return;

      formData.contact = {
        firstName: $fn.value.trim(),
        lastName:  $ln.value.trim(),
        email:     $email.value.trim(),
        phone:     $phone.value.trim(),
      };

      const $btn = $id('pip-book-now-btn');
      $btn.disabled = true;
      $btn.textContent = 'Booking\u2026';
      $bookErr.classList.remove('is-visible');

      // Build the ISO datetime string the API expects.
      // bookingTime may already be a full ISO string (preferred), or a plain
      // "HH:MM" string from the availability endpoint.
      function buildDatetime(date, timeValue, tz) {
        // Already a full ISO datetime — use as-is
        if (timeValue && (timeValue.includes('T') || timeValue.includes('Z') || /[+-]\d{2}:\d{2}$/.test(timeValue))) {
          return timeValue;
        }
        // Plain "HH:MM" string — combine with date and express in the selected tz
        const match = (timeValue || '').match(/(\d{1,2}):(\d{2})/);
        if (match && date) {
          const [y, mo, dy] = date.split('-').map(Number);
          const localDate = new Date(y, mo - 1, dy, parseInt(match[1], 10), parseInt(match[2], 10));
          // Format as ISO with offset using Intl
          const offsetStr = new Intl.DateTimeFormat('en-US', {
            timeZone: tz, timeZoneName: 'shortOffset',
          }).formatToParts(localDate).find(p => p.type === 'timeZoneName')?.value || '';
          const offsetMatch = offsetStr.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
          let offsetHours;
          if (offsetMatch) {
            const sign = offsetMatch[1];
            const hrs  = offsetMatch[2].padStart(2, '0');
            const mins = offsetMatch[3] || '00';
            offsetHours = `${sign}${hrs}:${mins}`;
          } else {
            offsetHours = '+00:00';
          }
          return `${date}T${String(parseInt(match[1], 10)).padStart(2,'0')}:${match[2]}:00${offsetHours}`;
        }
        return null;
      }

      try {
        const datetime = formData.isCallback
          ? null
          : buildDatetime(formData.bookingDate, formData.bookingTime, formData.bookingTz);

        // Normalize phone to E.164 with +1 prefix if not already present
        const rawPhone = formData.contact.phone.replace(/\D/g, '');
        let phone;
        if (rawPhone.length === 11 && rawPhone.startsWith('1')) {
          phone = `+${rawPhone}`;
        } else if (rawPhone.length === 10) {
          phone = `+1${rawPhone}`;
        } else {
          phone = `+${rawPhone}`;
        }

        const payload = {
          partner:      'habitaware_shopify',
          is_callback:  formData.isCallback,
          timezone:     formData.bookingTz || tzForState(formData.stateCode),
          country_code: 'US',
          first_name:   formData.contact.firstName,
          last_name:    formData.contact.lastName,
          email:        formData.contact.email,
          phone:        phone,
          state:        formData.stateName,
        };
        if (!formData.isCallback) {
          payload.datetime = datetime;
        }

        const res = await fetch(`${API_BASE}/v1/partner_intake_calls/book`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.message || errBody.error || `HTTP ${res.status}`);
        }

        markBookedInUrl();
        fireBookingConversionPixels();
        addToKlaviyoList(formData.contact, phone, datetime);
        showBookingSuccess();
      } catch (err) {
        console.error('[partner-intake] Booking failed:', err);
        $bookErr.textContent = err.message || 'Something went wrong \u2014 please try again.';
        $bookErr.classList.add('is-visible');
      } finally {
        $btn.disabled = false;
        $btn.textContent = 'Book now';
      }
    });

    // Append "#booked" to the page URL after a successful booking so URL-based
    // ad pixels (e.g. a Meta Custom Conversion on "URL contains #booked") can fire
    // without depending on a custom-event match. Uses history.replaceState to avoid
    // a new history entry and to prevent the browser from scrolling to an anchor.
    function markBookedInUrl() {
      try {
        if (window.location.hash === '#booked') return;
        const url = window.location.pathname + window.location.search + '#booked';
        window.history.replaceState(window.history.state, '', url);
        window.dispatchEvent(new HashChangeEvent('hashchange', {
          oldURL: window.location.href.replace(/#.*$/, ''),
          newURL: window.location.href,
        }));
      } catch (err) {
        console.warn('[partner-intake] markBookedInUrl failed:', err);
      }
    }

    // ── Meta / Google tags after successful booking ──
    function fireBookingConversionPixels() {
      const payload = {
        schedule_type: formData.isCallback ? 'callback' : 'scheduled',
        source: 'partner_intake_popup',
      };

      const fbName = (root.dataset.pipBookingFbEvent || '').trim();
      if (fbName && typeof window.fbq === 'function') {
        try {
          window.fbq('trackCustom', fbName, payload);
        } catch (err) {
          console.warn('[partner-intake] Meta Pixel trackCustom failed:', err);
        }
      }

      if (typeof window.gtag === 'function') {
        try {
          window.gtag('event', 'conversion', { send_to: PIP_GOOGLE_ADS_BOOKING_SEND_TO });
        } catch (err) {
          console.warn('[partner-intake] gtag conversion failed:', err);
        }
      }

      const gtagCustom = (root.dataset.pipBookingGtagEvent || '').trim();
      if (gtagCustom && typeof window.gtag === 'function') {
        try {
          window.gtag('event', gtagCustom, payload);
        } catch (err) {
          console.warn('[partner-intake] gtag custom event failed:', err);
        }
      }

      const gtmEvent = (root.dataset.pipBookingGtmEvent || '').trim();
      if (gtmEvent && window.dataLayer && typeof window.dataLayer.push === 'function') {
        try {
          window.dataLayer.push(Object.assign({ event: gtmEvent }, payload));
        } catch (err) {
          console.warn('[partner-intake] dataLayer push failed:', err);
        }
      }
    }

    // ── Klaviyo list subscription ─────────────────────────────────────────────────
    function addToKlaviyoList(contact, phone, datetime) {
      const tz = formData.bookingTz || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const dtObj = datetime ? new Date(datetime) : new Date();

      const callDatetime = new Intl.DateTimeFormat('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone: tz,
      }).format(dtObj);

      const callDate = new Intl.DateTimeFormat('en-US', {
        weekday: 'long', month: 'long', day: 'numeric',
        timeZone: tz,
      }).format(dtObj);

      const callTime = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone: tz,
      }).format(dtObj);

      const callDateShort = new Intl.DateTimeFormat('en-US', {
        month: 'long', day: 'numeric', year: 'numeric', timeZone: tz,
      }).format(dtObj);

      const profileProps = {
        scheduled_call_time:  callDatetime,
        call_date:            callDate,
        call_date_short:      callDateShort,
        call_time:            callTime,
        call_phone:           phone,
        call_timezone:        TZ_DISPLAY[tz] || tz,
        is_callback:          formData.isCallback ? 'true' : 'false',
        booking_state:        formData.stateName || '',
        booking_insurance:    formData.insuranceName || '',
      };

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=Yf7GWF', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2023-12-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email:        contact.email,
                    first_name:   contact.firstName,
                    last_name:    contact.lastName,
                    phone_number: phone,
                    properties:   profileProps,
                  },
                },
              },
            },
            relationships: {
              list: {
                data: { type: 'list', id: 'Wwn53s' },
              },
            },
          },
        }),
      }).catch((err) => console.warn('[partner-intake] Klaviyo subscription failed:', err));
    }

    // ── Booking success stage ─────────────────────────────────────────────────────
    function showBookingSuccess() {
      const $timingStep = $id('pip-success-step-timing');

      if (formData.isCallback) {
        $timingStep.textContent =
          'A NOCD Member Advocate with deep understanding of BFRBs will call you within the next 15 minutes.';
      } else {
        const [y, m, d] = formData.bookingDate.split('-').map(Number);
        const dateObj   = new Date(y, m - 1, d);
        const monthStr  = dateObj.toLocaleDateString('en-US', { month: 'long' });
        const timeLabel = formData.bookingTimeDisplay || formData.bookingTime || '';
        $timingStep.textContent =
          `A NOCD Member Advocate with deep understanding of BFRBs will call you at ${timeLabel} on ${ordinalDay(d)} ${monthStr}.`;
      }

      // Terminal stage — navigate first, then wipe history so goToStage's
      // push is discarded and the back button is correctly hidden.
      goToStage('booking-success');
      stageHistory = [];
      $backBtn.hidden = true;
    }

    // ── Finish button ─────────────────────────────────────────────────────────────
    const $finishBtn = $id('pip-finish-btn');
    if ($finishBtn) $finishBtn.addEventListener('click', () => closePopup(false));

    // ── Auto-open ─────────────────────────────────────────────────────────────────
    if (autoOpen) {
      setTimeout(openPopup, autoOpenDelay * 1000);
    }

    // ── Instance API ──────────────────────────────────────────────────────────────
    root._pipInstance = { open: openPopup, close: closePopup, formData };
  }

  // ── Bootstrap: initialise every popup on the page ────────────────────────────
  document.querySelectorAll('.pip-overlay').forEach(initPIP);

  // ── Replace NOCD anchor links with popup-opening buttons ─────────────────────
  // Runs after all instances are initialised. Uses the first popup found on the
  // page. Separated from initPIP so it works even if the popup section is added
  // to the page after this script loads (e.g. via the theme editor).
  function hookNocdLinks() {
    const overlay = document.querySelector('.pip-overlay');
    if (!overlay || !overlay._pipInstance) return;
    const openFn = overlay._pipInstance.open;

    document.querySelectorAll('a').forEach(function (el) {
      if (overlay.contains(el) || !/nocd/i.test(el.href)) return;

      el.setAttribute('role', 'button');
      el.removeAttribute('target');
      el.addEventListener('click', function (e) {
        e.preventDefault();
        openFn();
      });
    });
  }

  hookNocdLinks();

})();
